import React, { useEffect, useState } from "react";
import { Plus, Trash2, BadgeDollarSign, AlertTriangle } from "lucide-react";
import { api } from "../../lib/api";

export const AdminLedgerTab = ({ artists, refresh }) => {
  const [selected, setSelected] = useState(artists[0]?.slug || "");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({ type: "credit", amount: "", note: "" });
  const [error, setError] = useState("");

  const load = async (slug) => {
    if (!slug) return;
    setLoading(true);
    try {
      const r = await api.get(`/admin/artists/${slug}/ledger`);
      setData(r.data);
    } catch {
      setData(null);
    }
    setLoading(false);
  };

  useEffect(() => {
    if (!selected && artists[0]?.slug) setSelected(artists[0].slug);
  }, [artists]); // eslint-disable-line

  useEffect(() => {
    load(selected);
  }, [selected]); // eslint-disable-line

  const addEntry = async (e) => {
    e.preventDefault();
    setError("");
    try {
      await api.post(`/admin/artists/${selected}/ledger`, {
        type: form.type,
        amount: Number(form.amount),
        note: form.note || null,
      });
      setForm({ type: "credit", amount: "", note: "" });
      await load(selected);
      refresh && refresh();
    } catch (err) {
      setError(err?.response?.data?.detail || "Failed");
    }
  };

  const deleteEntry = async (id) => {
    if (!window.confirm("Delete this entry?")) return;
    await api.delete(`/admin/artists/${selected}/ledger/${id}`);
    await load(selected);
    refresh && refresh();
  };

  const markPaidOut = async () => {
    if (!window.confirm(`Mark balance €${data?.balance?.toFixed(2)} as PAID OUT?`)) return;
    try {
      await api.post(`/admin/artists/${selected}/ledger/payout`);
      await load(selected);
      refresh && refresh();
    } catch (err) {
      alert(err?.response?.data?.detail || "Failed");
    }
  };

  const wipeAll = async () => {
    if (!window.confirm(`WIPE ALL ledger entries for this artist? This cannot be undone.`)) return;
    if (!window.confirm("Really? Type confirmation by clicking OK again.")) return;
    await api.delete(`/admin/artists/${selected}/ledger`);
    await load(selected);
    refresh && refresh();
  };

  return (
    <section data-testid="admin-ledger-section">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
        <p className="font-mono text-xs tracking-[0.3em] text-kult-yellow">/ ARTIST LEDGER</p>
        <select
          value={selected}
          onChange={(e) => setSelected(e.target.value)}
          className="bg-kult-black border border-kult-border px-3 py-2 font-mono text-xs"
          data-testid="ledger-artist-select"
        >
          {artists.map((a) => (
            <option key={a.slug} value={a.slug}>{a.name}</option>
          ))}
        </select>
      </div>

      {loading || !data ? (
        <p className="font-mono text-xs text-kult-muted">LOADING...</p>
      ) : (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
            <Stat label="BALANCE" value={`€${(data.balance || 0).toFixed(2)}`} accent testId="ledger-balance" />
            <Stat label="SALES (LIFETIME)" value={`€${(data.sales_total || 0).toFixed(2)}`} />
            <Stat label="PAID OUT" value={`€${(data.paid_out || 0).toFixed(2)}`} />
            <Stat label="ENTRIES" value={data.entry_count} />
          </div>

          <form
            onSubmit={addEntry}
            className="border-2 border-kult-pink p-5 bg-kult-black2 mb-6 grid grid-cols-1 md:grid-cols-6 gap-3 items-end"
            data-testid="ledger-add-form"
          >
            <label className="md:col-span-1">
              <span className="font-mono text-[10px] tracking-[0.3em] text-kult-muted block mb-1">TYPE</span>
              <select
                value={form.type}
                onChange={(e) => setForm({ ...form, type: e.target.value })}
                className="w-full bg-kult-black border border-kult-border px-3 py-3 font-mono text-sm"
                data-testid="ledger-type"
              >
                <option value="credit">CREDIT (+)</option>
                <option value="debit">DEBIT (−)</option>
                <option value="payout">PAYOUT (−)</option>
                <option value="adjustment">ADJUSTMENT</option>
              </select>
            </label>
            <label className="md:col-span-1">
              <span className="font-mono text-[10px] tracking-[0.3em] text-kult-muted block mb-1">AMOUNT (€)</span>
              <input
                type="number" step="0.01" required min="0"
                value={form.amount}
                onChange={(e) => setForm({ ...form, amount: e.target.value })}
                className="w-full bg-transparent border border-kult-border px-3 py-3 font-mono text-sm"
                data-testid="ledger-amount"
              />
            </label>
            <label className="md:col-span-3">
              <span className="font-mono text-[10px] tracking-[0.3em] text-kult-muted block mb-1">NOTE</span>
              <input
                value={form.note}
                onChange={(e) => setForm({ ...form, note: e.target.value })}
                className="w-full bg-transparent border border-kult-border px-3 py-3 font-mono text-sm"
                placeholder="Bonus, advance, manual fix..."
                data-testid="ledger-note"
              />
            </label>
            <button
              type="submit"
              className="md:col-span-1 bg-kult-pink text-black font-display text-lg py-3 hover:bg-kult-yellow inline-flex items-center justify-center gap-1"
              data-testid="ledger-submit"
            >
              <Plus size={16} /> ADD
            </button>
            {error && <p className="md:col-span-6 font-mono text-xs text-kult-pink">{error}</p>}
          </form>

          <div className="flex flex-wrap gap-2 mb-6">
            <button
              onClick={markPaidOut}
              disabled={(data.balance || 0) <= 0}
              className="inline-flex items-center gap-2 bg-kult-yellow text-black px-4 py-2 font-mono text-xs tracking-[0.25em] disabled:opacity-40"
              data-testid="ledger-mark-paid"
            >
              <BadgeDollarSign size={14} /> MARK BALANCE AS PAID OUT
            </button>
            <button
              onClick={wipeAll}
              className="inline-flex items-center gap-2 border border-red-500 text-red-400 px-4 py-2 font-mono text-xs tracking-[0.25em] hover:bg-red-500/10"
              data-testid="ledger-wipe"
            >
              <AlertTriangle size={14} /> WIPE ALL ENTRIES
            </button>
          </div>

          <div className="border border-kult-border" data-testid="ledger-entries-list">
            <div className="grid grid-cols-12 gap-3 px-4 py-3 bg-kult-black2 border-b border-kult-border font-mono text-[10px] tracking-[0.3em] text-kult-muted">
              <span className="col-span-2">DATE</span>
              <span className="col-span-2">TYPE</span>
              <span className="col-span-2 text-right">AMOUNT</span>
              <span className="col-span-5">NOTE</span>
              <span className="col-span-1 text-right">×</span>
            </div>
            {data.entries.length === 0 ? (
              <p className="px-4 py-6 font-mono text-xs text-kult-muted">No entries yet.</p>
            ) : data.entries.map((e) => (
              <div key={e.id} className="grid grid-cols-12 gap-3 px-4 py-3 border-b border-kult-border last:border-b-0 items-center" data-testid={`ledger-entry-${e.id}`}>
                <span className="col-span-2 font-mono text-[11px] text-kult-muted">{new Date(e.created_at).toLocaleDateString()}</span>
                <span className="col-span-2 font-mono text-[10px] tracking-[0.25em] text-kult-yellow uppercase">{e.type}</span>
                <span className={`col-span-2 text-right font-mono text-sm ${e.amount >= 0 ? "text-white" : "text-kult-pink"}`}>
                  {e.amount >= 0 ? "+" : ""}€{e.amount.toFixed(2)}
                </span>
                <span className="col-span-5 font-mono text-xs text-kult-muted truncate">
                  {e.note || (e.order_number ? `Order ${e.order_number}` : "—")}
                </span>
                <button
                  onClick={() => deleteEntry(e.id)}
                  className="col-span-1 justify-self-end p-1.5 text-kult-muted hover:text-kult-pink"
                  data-testid={`ledger-delete-${e.id}`}
                  aria-label="Delete"
                >
                  <Trash2 size={14} />
                </button>
              </div>
            ))}
          </div>
        </>
      )}
    </section>
  );
};

function Stat({ label, value, accent, testId }) {
  return (
    <div className={`border-2 ${accent ? "border-kult-pink bg-kult-black2" : "border-kult-border"} p-4`} data-testid={testId}>
      <p className="font-mono text-[10px] tracking-[0.3em] text-kult-muted">{label}</p>
      <p className={`font-display text-2xl mt-1 ${accent ? "text-kult-pink" : "text-white"}`}>{value}</p>
    </div>
  );
}
